import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'

export interface TelephonySmsAttachedLead {
  id: string
  name: string
}

export interface TelephonySmsAttachedDriver {
  id: string
  name: string
  orgId: string
}

export type TelephonySmsDirection = 'inbound' | 'outbound'
export type TelephonySmsStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'received'
export type TelephonySmsVendor = 'aircall' | 'dialpad'

export interface TelephonySmsListItem {
  id: string
  vendor: TelephonySmsVendor
  direction: TelephonySmsDirection
  status: TelephonySmsStatus
  fromE164: string
  toE164: string
  body: string
  senderName: string | null
  attachedLead: TelephonySmsAttachedLead | null
  attachedDriver: TelephonySmsAttachedDriver | null
  error: string
  createdAt: string
}

export type TelephonySmsAttached = 'true' | 'false' | undefined
export type TelephonySmsDirectionFilter = 'inbound' | 'outbound' | undefined

export interface TelephonySmsFilters {
  page?: number
  direction?: TelephonySmsDirectionFilter
  attached?: TelephonySmsAttached
  failedOnly?: boolean
  /** Matches the text, either number, or the attached lead or its customer (name, DOT, ID). */
  q?: string
  /** A rep's InsureUser ID: what they handled, plus what reached their line unanswered. */
  userId?: string
  /** Inclusive `yyyy-MM-dd` bounds. */
  dateFrom?: string
  dateTo?: string
}

function getTelephonySms(params: TelephonySmsFilters): Promise<PaginatedResults<TelephonySmsListItem>> {
  const queryParams: Record<string, string> = {}
  if (params.page && params.page > 1) queryParams.page = String(params.page)
  if (params.direction) queryParams.direction = params.direction
  if (params.attached) queryParams.attached = params.attached
  if (params.failedOnly) queryParams.failed_only = 'true'
  if (params.q) queryParams.q = params.q
  if (params.userId) queryParams.user_id = params.userId
  if (params.dateFrom) queryParams.date_from = params.dateFrom
  if (params.dateTo) queryParams.date_to = params.dateTo
  return axios
    .get('/api/v1/insure/telephony/sms/', { params: queryParams })
    .then((r) => toCamelCase(r.data) as PaginatedResults<TelephonySmsListItem>)
}

export function useTelephonySms(params: TelephonySmsFilters) {
  return useQuery({
    queryKey: [CacheKeys.TELEPHONY_SMS, params],
    queryFn: () => getTelephonySms(params),
  })
}
